import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { adminApiService } from '../../services/adminApi';
import { apiService } from '../../services/api';
import { Tier } from '../../types';
import { TIERS, TIER_LABELS } from '../../utils/tiers';
import { useBackTo } from '../../hooks/useBackTo';
import { useFeedback } from '../../hooks/useFeedback';
import InlineFeedback from '../InlineFeedback';
import CopyIconButton from '../CopyIconButton';
import BillingMonthsTable from '../converter/BillingMonthsTable';
import { Badge, ErrorNotice, LoadingSpinner, formatNumber, inputClass, selectClass } from './shared';

// Returns null unless the input is a nonnegative safe integer.
function parseCreditTokens(input: string): number | null {
  const tokens = Number(input);
  return input.trim() === '' || !Number.isSafeInteger(tokens) || tokens < 0 ? null : tokens;
}

function AdminInstanceBilling() {
  const { id } = useParams<{ id: string }>();
  const instanceId = Number(id);
  const backHref = useBackTo('/admin/instances');
  const queryClient = useQueryClient();
  const feedback = useFeedback();
  const [saving, setSaving] = useState(false);
  const [creditInput, setCreditInput] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'instanceBilling', instanceId],
    queryFn: () => adminApiService.getInstanceBilling(instanceId),
    enabled: Number.isSafeInteger(instanceId) && instanceId > 0,
    retry: false,
  });

  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['admin', 'instanceBilling', instanceId] }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'instances'] }),
  ]);

  const changeTier = async (tier: Tier) => {
    setSaving(true);
    feedback.clear();
    try {
      await apiService.updateInstanceTier(instanceId, tier);
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: ['admin', 'instanceFeatures'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'tierFeatures'] }),
      ]);
      feedback.showSuccess('Instance tier updated.');
    } catch {
      feedback.showError('Failed to update instance tier.');
    } finally {
      setSaving(false);
    }
  };

  const changeCredits = async (action: 'add' | 'set') => {
    const tokens = parseCreditTokens(creditInput);
    if (saving || tokens === null) return;
    setSaving(true);
    feedback.clear();
    try {
      const result = await apiService.updateInstanceCredits(instanceId, action, tokens);
      await refresh();
      setCreditInput('');
      feedback.showSuccess(result.message);
    } catch (error: any) {
      feedback.showError(error.response?.data?.message || 'Failed to update instance credits.');
    } finally {
      setSaving(false);
    }
  };

  const instance = data?.instance;
  const notFound = (error as any)?.response?.status === 404 || !(Number.isSafeInteger(instanceId) && instanceId > 0);
  const persistent = instance?.instance_mode === 'persistent';
  const editable = persistent && instance.tier !== 'free' && instance.is_active;
  const creditsDisabled = saving || parseCreditTokens(creditInput) === null;

  return (
    <div>
      <Link to={backHref} className="text-sm text-secondary hover:text-body-color inline-flex items-center gap-1 mb-3">
        <ArrowLeftIcon className="w-4 h-4" />
        Back
      </Link>
      <InlineFeedback feedback={feedback.feedback} className="mb-4" />
      {notFound ? <ErrorNotice message="Instance not found." /> : error && <ErrorNotice message="Failed to load billing." />}
      {isLoading && <LoadingSpinner />}
      {instance && (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-heading">{instance.name}</h1>
              <Badge value={instance.instance_mode} />
              {!instance.is_active && <Badge value="revoked" />}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-mono text-xs text-secondary">{instance.instance_key}</span>
              <CopyIconButton text={instance.instance_key} label="instance key" iconClassName="w-3.5 h-3.5" />
            </div>
          </div>
          {!persistent ? (
            <p className="text-sm text-secondary">Temporary instances have no tier, credits, or billing.</p>
          ) : (
            <>
              <div className="bg-widget-bg rounded-lg border border-gray-700 p-6">
                <dl className="flex flex-wrap items-center gap-x-10 gap-y-3">
                  <div className="flex items-center gap-2">
                    <dt className="text-secondary">License tier</dt>
                    <dd>
                      <select aria-label="License tier" className={selectClass} value={instance.tier} disabled={!instance.is_active || saving} onChange={(e) => changeTier(e.target.value as Tier)}>
                        {TIERS.map((tier) => <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>)}
                      </select>
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <dt className="text-secondary">Credit balance</dt>
                    <dd className="flex items-center gap-2 text-heading">
                      <span>{formatNumber(instance.credit_tokens)} tokens</span>
                      {editable && (
                        <>
                          <input type="number" min="0" step="1" aria-label="Token credits" className={`${inputClass} w-32`} value={creditInput} disabled={saving} onChange={(e) => setCreditInput(e.target.value)} />
                          <button className="btn-secondary text-sm px-2 py-1" disabled={creditsDisabled} onClick={() => changeCredits('add')}>Add</button>
                          <button className="btn-secondary text-sm px-2 py-1" disabled={creditsDisabled} onClick={() => changeCredits('set')}>Set</button>
                        </>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
              <BillingMonthsTable months={data.months} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminInstanceBilling;
